/**
 * src/stores/chat.ts
 * --------------------
 * Zustand store for the active chat session's message state.
 *
 * Fully implemented this will:
 *   - Hold messages[], isTyping flag, streamingMessageId, and error state.
 *   - appendUserMessage(content) — optimistically adds the user message
 *     and opens an SSE stream via src/api/chat.ts.
 *   - handleStreamEvent(event) — reduces StreamEvent into the messages array:
 *       token events      → append to the streaming message's content
 *       tool_call events  → push/update tool call on the streaming message
 *       citation events   → push citation
 *       action events     → push action button
 *       done events       → replace streaming message with final message
 *       error events      → set error state, clear streaming flag
 *   - setReaction(messageId, reaction) — call reactToMessage() API
 *   - loadMessages(sessionId) — fetch history from getMessages() API
 *   - clearMessages() — reset for new session
 *
 * Key dependencies:
 *   zustand, src/api/chat.ts, src/types/message.ts
 */
import { create } from 'zustand'
import type { Message, StreamEvent, Reaction } from '@/types/message'

interface ChatStore {
  messages: Message[]
  isTyping: boolean
  streamingMessageId: string | null
  error: string | null

  appendMessage: (msg: Message) => void
  handleStreamEvent: (event: StreamEvent) => void
  setReaction: (messageId: string, reaction: Reaction) => void
  loadMessages: (msgs: Message[]) => void
  clearMessages: () => void
  setError: (error: string | null) => void
}

export const useChatStore = create<ChatStore>((set, get) => ({
  messages: [],
  isTyping: false,
  streamingMessageId: null,
  error: null,

  appendMessage: (msg) =>
    set((s) => ({ messages: [...s.messages, msg] })),

  handleStreamEvent: (event) => {
    const { streamingMessageId } = get()

    if (event.type === 'progress' && streamingMessageId && event.acknowledgment) {
      set((s) => {
        const index = s.messages.findIndex(m => m.id === streamingMessageId && m.isStreaming && !m.content)
        if (index < 0) return s
        const pending = s.messages[index]
        const acknowledgment: Message = event.message ?? {
          id: `${streamingMessageId}:ack`, sessionId: pending.sessionId, role: 'assistant',
          kind: 'acknowledgment', content: event.acknowledgment!, createdAt: new Date().toISOString(),
        }
        if (s.messages.some(m => m.id === acknowledgment.id)) return s
        return { messages: [...s.messages.slice(0, index), acknowledgment, ...s.messages.slice(index)] }
      })
    } else if (event.type === 'token' && event.token != null) {
      set((s) => ({
        isTyping: true,
        messages: s.messages.map((m) =>
          m.id === streamingMessageId
            ? { ...m, content: m.content + event.token, isStreaming: true }
            : m,
        ),
      }))
    } else if (event.type === 'tool_call' && event.toolCall) {
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === streamingMessageId
            ? { ...m, toolCalls: [...(m.toolCalls ?? []), event.toolCall!] }
            : m,
        ),
      }))
    } else if (event.type === 'citation' && event.citation) {
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === streamingMessageId
            ? { ...m, citations: [...(m.citations ?? []), event.citation!] }
            : m,
        ),
      }))
    } else if (event.type === 'action' && event.action) {
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === streamingMessageId
            ? { ...m, actions: [...(m.actions ?? []), event.action!] }
            : m,
        ),
      }))
    } else if (event.type === 'done' && event.message) {
      set((s) => ({
        isTyping: false,
        streamingMessageId: null,
        messages: streamingMessageId
          ? s.messages.map((m) =>
              m.id === streamingMessageId ? event.message! : m,
            )
          : [...s.messages, event.message!],
      }))
    } else if (event.type === 'human_takeover') {
      // A live agent is handling this chat — drop the empty AI placeholder;
      // the agent's reply arrives via polling (useChat).
      set((s) => ({
        isTyping: false,
        streamingMessageId: null,
        messages: s.messages.filter((m) => m.id !== streamingMessageId),
      }))
    } else if (event.type === 'error') {
      // Drop the placeholder we optimistically appended: leaving it behind
      // showed the customer an empty assistant bubble next to the error.
      set((s) => ({
        isTyping: false,
        streamingMessageId: null,
        error: event.error ?? 'An error occurred.',
        messages: s.messages.filter(
          (m) => !(m.id === streamingMessageId && !m.content),
        ),
      }))
    }
  },

  setReaction: (messageId, reaction) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === messageId ? { ...m, reaction } : m,
      ),
    })),

  loadMessages: (msgs) => set({ messages: msgs }),

  clearMessages: () =>
    set({ messages: [], isTyping: false, streamingMessageId: null, error: null }),

  setError: (error) => set({ error }),
}))
